// pms-core / domain — « Tetris »: which unit to give a stay that has none.
//
// GLOSSARY `assignmentProposal` (« proposition d'attribution »). NOT
// `allocation`: that word is taken by the lettrage (« Imputation »).
//
// A FUNCTIONAL CORE in ARCHITECTURE.md §1's sense — types and pure functions
// over rows a port already handed us. It proposes; it never assigns. The
// assignment itself stays with `pms_assign_room_stay`, whose exclusion
// constraint is the enforcer of « one guest per unit per night ».
//
// ── v1 scope (operator, 2026-09-30) ──────────────────────────────────────────
//
// An ADVISORY help for allocating in Vega. Until cutover a seeded booking's
// room comes from Vega every 30 minutes, so a room set here would be put back;
// the desk reads the proposal and does the allocation in Vega.
//
// Deliberately NOT in v1, and the reason the staff call it Tetris: moving
// stays that are ALREADY allocated to dig out a slot large enough. v1 only
// looks at the gaps as they stand. Each unassigned stay is also proposed on
// its own — two unassigned stays can be offered the same room.
//
// ── The rules ────────────────────────────────────────────────────────────────
//
// A room is never proposed when it fails a HARD constraint:
//   * it has no category (a unit the operator has not typed is not sellable);
//   * any night of the stay is taken by another live stay;
//   * it cannot hold the party (adults + children — infants are a cot, D1);
//   * the guest asked for a bed configuration the room cannot be made up in.
//
// Among the rest, the booked CATEGORY comes first; rooms of other categories
// follow, flagged — but only categories that DOMINATE the booked one, in
// standing and in size (`roomFit.ts`, operator Q9 2026-10-01). Never below:
// that is what the measured 99.6 % says the desk does, and what the operator
// said it must. Within the others, the smallest room that fits comes first.
//
// The bed is checked by LAYOUT, not by name (`roomMeetsBed`): a Twin fits a
// TSUP made up as Triple. The first version compared names, which never
// offered one (brief §5).
//
// Then the Tetris ranking, in the order the operator approved:
//   1. no small hole — a gap of 1–2 free nights stranded between two stays is
//      close to unsellable;
//   2. a snug fit — back-to-back with the previous departure / next arrival;
//   3. the bed is already made up as requested — no change for the étage;
//   4. a multi-room booking lands on the floor of its other rooms;
// and as tie-breaks: the tightest free block (best fit — keeps long gaps for
// long stays), then the room number.

import { pmsNightCount } from './pms.js';
import { bedStandsFor, categoryDominates, roomMeetsBed, soldCategory, type RoomFit } from './roomFit.js';
import type { IsoDate, Room, RoomId, RoomStay, RoomTypeId } from './types.js';

/** A gap of at most this many free nights, with a stay on BOTH sides, counts
 *  as stranded. Two because a 3-night gap still sells as a weekend. */
export const ASSIGNMENT_SMALL_HOLE_NIGHTS = 2;

/** What the ranking needs from a unit. */
export type AssignableRoom = Pick<
  Room,
  'id' | 'roomNumber' | 'floor' | 'roomTypeId' | 'maxOccupancy' | 'configOptions' | 'currentBedConfig'
>;

/** What the ranking needs from a stay — the one being placed, and every stay
 *  already holding a unit. */
export type AssignableStay = Pick<
  RoomStay,
  | 'id'
  | 'reservationId'
  | 'roomId'
  | 'roomTypeId'
  | 'soldRoomTypeId'
  | 'checkinDate'
  | 'checkoutDate'
  | 'adults'
  | 'children'
  | 'requestedBedConfig'
>;

/** The stay on one side of the proposed slot, and how many free nights lie
 *  between them (0 = back-to-back). */
export interface AssignmentNeighbour {
  stayId: RoomStay['id'];
  gapNights: number;
}

export interface AssignmentProposal {
  roomId: RoomId;
  roomNumber: string;
  roomTypeId: RoomTypeId | null;
  /** false = « autre catégorie » — offered after every room of the booked one. */
  sameCategory: boolean;
  /** The last stay leaving this unit before the arrival; null = none. */
  before: AssignmentNeighbour | null;
  /** The first stay arriving after the departure; null = none. */
  after: AssignmentNeighbour | null;
  /** Free nights STRANDED before / after the stay: a gap of 1–2 nights with a
   *  stay on both sides, entirely ahead of today. 0 = none on that side. */
  strandedBefore: number;
  strandedAfter: number;
  /** How many sides strand nights (0–2) — what the ranking compares. */
  smallHoles: number;
  /** true = already made up as asked · false = the étage must change it ·
   *  null = no request, or nobody has said how the room stands. */
  bedReady: boolean | null;
  /** Places beyond the party; 0 is an exact fit. null = capacity not known. */
  spareBeds: number | null;
  /** Same floor as one of the booking's other ASSIGNED logements; null when it
   *  has none. */
  nearSiblings: boolean | null;
}

/** Why rooms OF THE BOOKED CATEGORY were not proposed — each room counted
 *  once, under the first rule it fails. What the desk needs to read an empty
 *  list: « 11 DSTD occupées » is an answer, silence is not. */
export interface AssignmentRejections {
  occupied: number;
  capacity: number;
  bed: number;
}

export interface AssignmentResult {
  /** Every feasible room, best first. */
  proposals: AssignmentProposal[];
  rejected: AssignmentRejections;
}

const overlaps = (a: AssignableStay, b: AssignableStay) =>
  a.checkinDate < b.checkoutDate && b.checkinDate < a.checkoutDate;

/**
 * Rank the units a stay could go into.
 *
 * `stays` is every stay that HOLDS a unit (the rack's occupying statuses); the
 * target itself, and any stay with no room, are ignored for occupancy. The
 * target's own `roomId` is not consulted — proposing for a stay that already
 * has a room is allowed and simply ignores where it is.
 */
export function proposeRoomAssignment(
  target: AssignableStay,
  rooms: readonly AssignableRoom[],
  stays: readonly AssignableStay[],
  todayIso: IsoDate,
  fit: RoomFit,
): AssignmentResult {
  const persons = target.adults + target.children;
  const wanted = target.requestedBedConfig ?? null;
  const placed = stays.filter((s) => s.id !== target.id && s.roomId !== null);

  const floorOf = new Map(rooms.map((r) => [r.id, r.floor]));
  const siblingFloors = placed
    .filter((s) => s.reservationId === target.reservationId)
    .map((s) => floorOf.get(s.roomId!))
    .filter((f): f is number => f !== undefined);

  const rejected: AssignmentRejections = { occupied: 0, capacity: 0, bed: 0 };
  const proposals: AssignmentProposal[] = [];

  // The booked category is the one the stay was SOLD as, not the type of any room it sits in.
  const sold = soldCategory(target);
  for (const room of rooms) {
    if (room.roomTypeId === null) continue;
    const sameCategory = room.roomTypeId === sold;
    // Below the booked category is not a proposal at all, not even flagged.
    if (!categoryDominates(fit, room.roomTypeId, sold)) continue;
    const reject = (why: keyof AssignmentRejections) => {
      if (sameCategory) rejected[why] += 1;
    };

    const inRoom = placed.filter((s) => s.roomId === room.id);
    if (inRoom.some((s) => overlaps(s, target))) {
      reject('occupied');
      continue;
    }
    if (room.maxOccupancy !== null && room.maxOccupancy < persons) {
      reject('capacity');
      continue;
    }
    if (!roomMeetsBed(fit, room, wanted)) {
      reject('bed');
      continue;
    }

    const prior = inRoom
      .filter((s) => s.checkoutDate <= target.checkinDate)
      .sort((a, b) => b.checkoutDate.localeCompare(a.checkoutDate))[0];
    const next = inRoom
      .filter((s) => s.checkinDate >= target.checkoutDate)
      .sort((a, b) => a.checkinDate.localeCompare(b.checkinDate))[0];
    const before =
      prior === undefined
        ? null
        : { stayId: prior.id, gapNights: pmsNightCount(prior.checkoutDate, target.checkinDate) };
    const after =
      next === undefined
        ? null
        : { stayId: next.id, gapNights: pmsNightCount(target.checkoutDate, next.checkinDate) };

    // A hole counts only when both its edges are stays and it lies ahead of
    // today: nights already past cannot be sold, so they are no loss.
    const small = (g: number) => g >= 1 && g <= ASSIGNMENT_SMALL_HOLE_NIGHTS;
    const strandedBefore =
      before !== null && prior!.checkoutDate >= todayIso && small(before.gapNights) ? before.gapNights : 0;
    const strandedAfter = after !== null && small(after.gapNights) ? after.gapNights : 0;

    // The room stands as asked when someone said so, or when it can only ever
    // be made up one way — by layout, like the check above.
    const bedReady = bedStandsFor(fit, room, wanted);

    proposals.push({
      roomId: room.id,
      roomNumber: room.roomNumber,
      roomTypeId: room.roomTypeId,
      sameCategory,
      before,
      after,
      strandedBefore,
      strandedAfter,
      smallHoles: (strandedBefore > 0 ? 1 : 0) + (strandedAfter > 0 ? 1 : 0),
      bedReady,
      spareBeds: room.maxOccupancy === null ? null : room.maxOccupancy - persons,
      nearSiblings: siblingFloors.length === 0 ? null : siblingFloors.includes(room.floor),
    });
  }

  proposals.sort(compareProposals);
  return { proposals, rejected };
}

const snugSides = (p: AssignmentProposal) =>
  (p.before?.gapNights === 0 ? 1 : 0) + (p.after?.gapNights === 0 ? 1 : 0);

/** How loose the free block around the stay is: first how many sides are
 *  OPEN (nothing booked beyond), then the free nights on the bounded ones. Not
 *  one sum — an open side would be infinite and swamp the finite side it is
 *  meant to be compared on. */
const blockSlack = (p: AssignmentProposal): [number, number] => [
  (p.before === null ? 1 : 0) + (p.after === null ? 1 : 0),
  (p.before?.gapNights ?? 0) + (p.after?.gapNights ?? 0),
];

const bedRank = (b: boolean | null) => (b === true ? 0 : b === null ? 1 : 2);

function compareProposals(a: AssignmentProposal, b: AssignmentProposal): number {
  if (a.sameCategory !== b.sameCategory) return a.sameCategory ? -1 : 1;
  // Only BETWEEN categories does size lead: inside the booked one the rooms
  // are alike, and a spare bed must not outrank the Tetris rules below.
  if (!a.sameCategory) {
    const sa = a.spareBeds ?? Number.POSITIVE_INFINITY;
    const sb = b.spareBeds ?? Number.POSITIVE_INFINITY;
    if (sa !== sb) return sa - sb;
  }
  if (a.smallHoles !== b.smallHoles) return a.smallHoles - b.smallHoles;
  if (snugSides(a) !== snugSides(b)) return snugSides(b) - snugSides(a);
  if (bedRank(a.bedReady) !== bedRank(b.bedReady)) return bedRank(a.bedReady) - bedRank(b.bedReady);
  if ((a.nearSiblings === true) !== (b.nearSiblings === true)) return a.nearSiblings === true ? -1 : 1;
  const [openA, gapA] = blockSlack(a);
  const [openB, gapB] = blockSlack(b);
  if (openA !== openB) return openA - openB;
  if (gapA !== gapB) return gapA - gapB;
  return a.roomNumber.localeCompare(b.roomNumber, 'fr', { numeric: true });
}
